/**
 * Validation logic for the Create String Group form.
 *
 * Pure functions — no side effects, no React dependency. Returns null when
 * valid, a localized user-facing error string otherwise.
 */

import {
  SG_NAME_MIN_LENGTH,
  SG_NAME_MAX_LENGTH,
  SG_NAME_PATTERN,
  SG_DESCRIPTION_MAX_LENGTH,
  SG_TEAM_NAME_MAX_LENGTH,
} from '../constants';

import type { SelectProps } from '@amzn/awsui-components-console';
import type { CreateStringGroupFormData } from '../types';
import type { TranslateFn } from 'src/i18n/shim/useTranslation';

/** An id rather than the text: the check runs in `useCreateStringGroupForm`, which stores the
 * already-resolved message in state. */
export const NAME_TAKEN_ERROR_ID = 'sg-validation-name-taken';

/**
 * True when the name satisfies every rule the form advertises: the
 * `SG_NAME_MIN_LENGTH` product minimum, the `SG_NAME_MAX_LENGTH` maximum, and
 * the `SG_NAME_PATTERN` character set mirrored from the Smithy
 * `StringGroupIdentifier` constraint (which is why spaces are rejected).
 *
 * Shared by `getNameError` and `isFormValid` so the field error and the submit
 * button can never disagree.
 */
function isNameValid(name: string): boolean {
  const trimmed = name.trim();
  return (
    trimmed.length >= SG_NAME_MIN_LENGTH &&
    trimmed.length <= SG_NAME_MAX_LENGTH &&
    SG_NAME_PATTERN.test(trimmed)
  );
}

export function getNameError(
  t: TranslateFn,
  name: string,
  showValidation: boolean,
): string | null {
  if (!showValidation) return null;
  const trimmed = name.trim();
  if (trimmed === '') return t('sg-validation-name-required');
  if (trimmed.length < SG_NAME_MIN_LENGTH)
    return t('sg-validation-name-too-short', { min: SG_NAME_MIN_LENGTH });
  if (trimmed.length > SG_NAME_MAX_LENGTH)
    return t('sg-validation-name-too-long', { max: SG_NAME_MAX_LENGTH });
  if (!SG_NAME_PATTERN.test(trimmed)) return t('sg-validation-name-pattern');
  return null;
}

export function getDescriptionError(
  t: TranslateFn,
  description: string,
  showValidation: boolean,
): string | null {
  if (!showValidation) return null;
  if (description.trim() === '') return t('sg-validation-description-required');
  if (description.trim().length > SG_DESCRIPTION_MAX_LENGTH)
    return t('sg-validation-description-too-long', {
      max: SG_DESCRIPTION_MAX_LENGTH,
    });
  return null;
}

/**
 * Both members of the submit contract's `StringGroupCreateMetadata` are
 * `@required` with `@length(min: 1)`, so a blank team name is a server 400 —
 * hence "required" here rather than merely advisory. The maximum mirrors the
 * structure's `@length(max: 256)` for the same reason.
 */
export function getTeamNameError(
  t: TranslateFn,
  teamName: string,
  showValidation: boolean,
): string | null {
  if (!showValidation) return null;
  const trimmed = teamName.trim();
  if (trimmed === '') return t('sg-validation-team-name-required');
  if (trimmed.length > SG_TEAM_NAME_MAX_LENGTH)
    return t('sg-validation-team-name-too-long', {
      max: SG_TEAM_NAME_MAX_LENGTH,
    });
  return null;
}

export function getSourceLocaleError(
  t: TranslateFn,
  sourceLocale: SelectProps.Option | null,
  showValidation: boolean,
): string | null {
  if (!showValidation) return null;
  if (!sourceLocale) return t('sg-validation-source-locale-required');
  return null;
}

export function getCxFamilyError(
  t: TranslateFn,
  cxFamilyId: string,
  showValidation: boolean,
): string | null {
  if (!showValidation) return null;
  if (!cxFamilyId) return t('sg-validation-cx-family-required');
  return null;
}

export function getCxIdsError(
  t: TranslateFn,
  selectedCount: number,
  showValidation: boolean,
): string | null {
  if (!showValidation) return null;
  if (selectedCount === 0) return t('sg-validation-cx-ids-required');
  return null;
}

/** Returns true when all required fields pass validation. */
export function isFormValid(data: CreateStringGroupFormData): boolean {
  return (
    isNameValid(data.name) &&
    data.description.trim() !== '' &&
    data.description.trim().length <= SG_DESCRIPTION_MAX_LENGTH &&
    data.teamName.trim() !== '' &&
    data.teamName.trim().length <= SG_TEAM_NAME_MAX_LENGTH &&
    data.cxFamilyId.trim() !== '' &&
    data.cxIds.length > 0 &&
    data.sourceLocale !== null
  );
}
